import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { _verify_bonus_code, _request_sms_verification } from "../common/axios_services";
import { fetchData, profileData, setUserData, setToken } from "../slices/profileSlice";
import { Spin, Alert, Button, Input, Form, Card, message } from "antd";
import {
  GiftOutlined,
  CheckCircleFilled,
  InfoCircleFilled,
  SafetyCertificateOutlined,
  LoadingOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

const RedeemBonusCodePage = () => {
  const [searchParams] = useSearchParams();
  const { code: routeCode } = useParams();
  const location = useLocation();
  const urlCode = searchParams.get("code") || routeCode || "";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const currentUser = useSelector((state) => state.userProfile?.user);
  const signupUser = location.state?.signupUserData;
  const initialPhone =
    signupUser?.phoneNumber || currentUser?.phoneNumber || "";

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState(null);
  const [statusState, setStatusState] = useState(null);

  const antIcon = <LoadingOutlined style={{ fontSize: 40, color: "#16a34a" }} spin />;

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    if (initialPhone) {
      form.setFieldsValue({ phone: initialPhone });
    }
  }, [initialPhone, form]);

  const handleVerify = async (values) => {
    const rawCode = values?.code || urlCode;
    const cleanCode = (rawCode || "").trim();

    if (!cleanCode) {
      setError("Please enter your verification bonus code.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await _verify_bonus_code(cleanCode, values?.phone || initialPhone);
      const data = res?.data;

      if (data?.tokens?.accessToken) {
        localStorage.setItem("konnect_token", data.tokens.accessToken);
        localStorage.setItem("token", data.tokens.accessToken);
        if (data.tokens.refreshToken) {
          localStorage.setItem("refreshToken", data.tokens.refreshToken);
        }
        dispatch(setToken(data.tokens.accessToken));
      }

      if (data?.user) {
        dispatch(setUserData(data.user));
        dispatch(profileData(data.user));
        dispatch(fetchData(true));
      }

      if (data?.alreadyClaimed) {
        setStatusState({
          type: "alreadyClaimed",
          message: data.message || "Welcome bonus already claimed!",
        });
      } else {
        setStatusState({
          type: "success",
          message: data?.message || "Welcome bonus claimed successfully!",
          amount: data?.bonusAmount ?? 0,
        });
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Verification code is invalid or has expired.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    setResending(true);
    try {
      await _request_sms_verification();
      setCountdown(60);
      message.success("A new verification code has been sent to your phone via SMS.");
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to resend SMS verification code."
      );
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (urlCode && urlCode.trim().length >= 4) {
      form.setFieldsValue({ code: urlCode.trim() });
      handleVerify({ code: urlCode.trim() });
    }
  }, [urlCode]);

  return (
    <div className="min-h-screen bg-gray-50/60 px-4 py-8 pb-40 flex flex-col items-center justify-center">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-100 text-green-600 mb-3 shadow-sm">
            <GiftOutlined style={{ fontSize: 32 }} />
          </div>
          <h1 className="text-2xl font-gilroyBold text-gray-900 mb-1">
            Claim Welcome Bonus
          </h1>
          <p className="text-gray-600 text-lg">
            Enter your code below to claim your welcome bonus
          </p>
        </div>

        {statusState?.type === "success" && (
          <Card className="rounded-2xl shadow-sm border-green-200 bg-white text-center py-6 px-2">
            <CheckCircleFilled className="text-green-600 text-5xl mb-4" />
            <h2 className="text-2xl font-gilroyBold text-green-900 mb-2">
              Congratulations! Your ₦{Number(statusState.amount || 5000).toLocaleString('en-US')} Bonus is Ready
            </h2>
            <div className="my-4 py-3 px-4 bg-green-50 rounded-2xl border border-green-100 inline-block">
              <span className="font-semibold text-green-700 block">Bonus Added</span>
              <span className="text-3xl font-gilroyBold text-green-600">
                +₦{Number(statusState.amount || 5000).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <p className="text-gray-600 text-lg mb-6">
              {statusState.message || "Use it to buy food, electronics, and lots more."}
            </p>
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/home")}
              className="bg-green-600 hover:bg-green-700 border-none font-gilroyBold h-12 px-8 rounded-2xl inline-flex items-center gap-2"
            >
              <span>Visit Home Page</span>
              <ArrowRightOutlined />
            </Button>
          </Card>
        )}

        {statusState?.type === "alreadyClaimed" && (
          <Card className="rounded-2xl shadow-sm border-amber-200 bg-white text-center py-6 px-2">
            <InfoCircleFilled className="text-amber-500 text-5xl mb-4" />
            <h2 className="text-2xl font-gilroyBold text-gray-900 mb-2">
              Bonus Already Claimed
            </h2>
            <p className="text-gray-700 text-lg mb-6">
              {statusState.message}
            </p>
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/home")}
              className="bg-amber-600 hover:bg-amber-700 border-none font-gilroyBold h-12 px-8 rounded-2xl inline-flex items-center gap-2"
            >
              <span>Visit Home Page</span>
              <ArrowRightOutlined />
            </Button>
          </Card>
        )}

        {!statusState && (
          <Card className="rounded-2xl shadow-sm border-gray-200 bg-white p-2">
            {error && (
              <Alert
                message="Unable to Redeem"
                description={error}
                type="error"
                showIcon
                className="mb-4 rounded-xl text-base"
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleVerify}
              initialValues={{ code: urlCode, phone: initialPhone }}
            >
              <Form.Item
                label={<span className="text-lg font-semibold text-gray-800">Bonus Code</span>}
                name="code"
                rules={[
                  { required: true, message: "Please enter your 6-digit bonus code" },
                ]}
              >
                <Input
                  size="large"
                  placeholder="e.g. 123456"
                  className="rounded-2xl text-center font-gilroyBold text-2xl tracking-widest h-14"
                  maxLength={12}
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                label={<span className="text-lg font-medium text-gray-600">Phone Number (optional)</span>}
                name="phone"
              >
                <Input
                  size="large"
                  placeholder="e.g. 08012345678"
                  className="rounded-2xl text-lg h-12"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item className="mb-2 mt-6">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  className="rounded-2xl font-gilroyBold text-lg h-14 bg-green-600 hover:bg-green-700 border-none shadow-md flex items-center justify-center gap-2"
                >
                  <SafetyCertificateOutlined /> Claim Welcome Bonus
                </Button>
              </Form.Item>

              <div className="flex items-center justify-between mt-4 px-2">
                <Button
                  type="link"
                  className="p-0 text-green-700 hover:text-green-800 font-medium  flex items-center gap-1"
                  loading={resending}
                  disabled={countdown > 0}
                  onClick={handleResendCode}
                >
                  <ReloadOutlined />
                  {countdown > 0
                    ? `Resend Code in ${countdown}s`
                    : "Resend Code via SMS"}
                </Button>

                <Button
                  type="link"
                  className="p-0 text-gray-500 hover:text-gray-800 font-medium "
                  onClick={() => navigate("/home")}
                >
                  Skip to Home
                </Button>
              </div>
            </Form>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RedeemBonusCodePage;
